import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { RootState, store } from "../../utils/store";
import { Job, READY_STATES, TaskResultReportSuccess } from "./types";
import { retryJob } from "../../api/jobsApi";
import { findRetryableDocumentIds, friendlyStatus } from "./utils";
import { Button, Icon } from "../../components/tailwind";
import { bulkActionLabels, BulkAction } from "../../ui/BulkActionsDropdown";
import { useGetAuthQuery } from "../../api/authApi";
import { Spinner } from "../../utils/Spinner";
import { JobProgressbarHandler } from "./JobProgressbarHandler";
import { JobsSummaryTableTabs } from "./JobsTableTabs";
import { SSEClient } from "../../api/SSEApi";
import { NOOP } from "../..";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../api/djangoToastSlice";

type Props = {
  jobId: string;
};

/**
 * Downloads a file by creating a temporary link element and triggering a click
 */
const downloadFile = (fileUrl: string, fileName?: string): void => {
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = fileUrl;
  if (fileName) {
    link.download = fileName;
  }
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const JobDetail: React.FC<Props> = ({ jobId }) => {
  const { data: auth } = useGetAuthQuery();
  const token = useSelector((state: RootState) => state.auth.token);

  const [retryClicked, setRetryClicked] = useState(false);
  const [job, setJob] = useState<Job | null>(null);

  const [showFullArguments, setShowFullArguments] = useState(false);
  const downloadTriggeredRef = useRef(false);

  const [streamIsActive, setStreamIsActive] = useState(false);
  const startStream = useCallback(() => {
    if (streamIsActive)
      return {
        close: NOOP,
      };
    const sseClient = new SSEClient<Job & { close?: boolean }>(token);
    setStreamIsActive(true);
    sseClient.connect(
      `/api/_internal/customer/jobs/${jobId}/stream/`,
      (data) => {
        setJob(data);
      },
      () => {
        setStreamIsActive(false);
      },
      (_, msg) => {
        setStreamIsActive(false);
        store.dispatch(
          setDjangoToastOpen({
            appearance: NotificationAppearance.ERROR,
            content: msg,
            additionalContent: "Job Status Stream ERROR",
          })
        );
      }
    );
    return {
      close: (): void => {
        sseClient.close();
        setStreamIsActive(false);
      },
    };
  }, [token, job, streamIsActive, setStreamIsActive]);

  useEffect(() => {
    if (!token || !jobId) {
      return;
    }
    const { close } = startStream();
    return () => {
      close();
    };
  }, [token, jobId]);

  const userIsStaff = useMemo(() => {
    if (!auth) return false;
    return auth.user.is_staff;
  }, [auth]);

  const fullArguments = JSON.stringify(job?.arguments, null, 2);
  const shortArguments =
    fullArguments?.length > 300 ? fullArguments.slice(0, 300) + "..." : "";

  const retryableDocumentIds = useMemo(() => {
    return findRetryableDocumentIds(job);
  }, [job]);

  const OnRetryFailedDocuments = (ids: string[]): void => {
    setRetryClicked(true);
    retryJob(token, job?.id, ids).then(() => {
      setRetryClicked(false);
      startStream();
    });
  };

  const fetchingJob = useMemo(() => {
    return job ? false : true;
  }, [job]);

  useEffect(() => {
    let timeout: number;
    const queryString = location.search;
    const urlParams = new URLSearchParams(queryString);
    const autoRedirect = !!urlParams.get("auto_redirect");

    // Check for custom redirect_url from task meta, fallback to all_product_list_url
    const customRedirectUrl = job?.last_run_task?.result?.redirect_url;
    const redirectUrl =
      customRedirectUrl && customRedirectUrl.trim()
        ? customRedirectUrl
        : job?.all_product_list_url;

    if (!autoRedirect || !redirectUrl) return;
    if (READY_STATES.includes(job?.status_from_tasks_result)) {
      store.dispatch(
        setDjangoToastOpen({
          appearance: NotificationAppearance.INFO,
          content: "Job Done... Redirecting",
        })
      );
      timeout = (setTimeout(() => {
        window.location.replace(redirectUrl);
      }, 2000) as unknown) as number;
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    job?.status_from_tasks_result,
    job?.all_product_list_url,
    job?.last_run_task?.result?.redirect_url,
  ]);

  // Auto-download file when DownloadAction job is successful
  useEffect(() => {
    // Only trigger for DownloadAction jobs
    if (job?.action_name !== BulkAction.DOWNLOAD) {
      return;
    }

    // Only trigger when job is successful
    if (job?.status_from_tasks_result !== "SUCCESS") {
      return;
    }

    // Only trigger once
    if (downloadTriggeredRef.current) {
      return;
    }

    // Check if job has summary with successful reports
    if (!job?.summary) {
      return;
    }

    // Find the first successful report with a resource_url
    const summaries = Object.values(job.summary);
    const successfulSummary = summaries.find(
      (summary) => !("reason" in summary.last_report)
    );

    if (!successfulSummary) {
      return;
    }

    const report = successfulSummary.last_report as TaskResultReportSuccess;
    if (!report?.resource_url) {
      return;
    }

    // Extract filename from resource_name or resource_url
    const fileName =
      report.resource_name || report.resource_url.split("/").pop() || undefined;

    // Trigger download
    downloadTriggeredRef.current = true;
    downloadFile(report.resource_url, fileName);

    store.dispatch(
      setDjangoToastOpen({
        appearance: NotificationAppearance.INFO,
        content: "Download started automatically",
      })
    );
  }, [job?.action_name, job?.status_from_tasks_result, job?.summary]);

  return (
    <>
      <h1>Job {jobId}</h1>
      <div className="txu-log-item">
        <h5 className="txu-log-item-label">Action</h5>
        <div className="txu-log-item-value">
          {fetchingJob ? (
            <Spinner align="left" />
          ) : (
            bulkActionLabels?.[job?.action_name] ?? job?.action_name
          )}
        </div>
      </div>
      <div className="txu-log-item">
        <h5 className="txu-log-item-label">User</h5>
        <div className="txu-log-item-value">
          {fetchingJob ? <Spinner align="left" /> : job?.user_email || "—"}
        </div>
      </div>

      <div className="txu-log-item">
        <h5 className="txu-log-item-label">Status</h5>
        <div className="txu-log-item-value">
          {fetchingJob ? (
            <Spinner align="left" />
          ) : (
            <>
              {friendlyStatus(job?.status_from_tasks_result)}{" "}
              {streamIsActive && (
                <span className="tw-text-xs tw-text-gray-500 tw-animate-pulse">
                  Checking Status...
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="txu-log-item">
        <h5 className="txu-log-item-label">Created</h5>
        <div className="txu-log-item-value">
          {fetchingJob ? <Spinner align="left" /> : job?.created}
        </div>
      </div>
      <div className="txu-log-item">
        <h5
          className={`txu-log-item-label ${
            retryClicked ? "tw-animate-pulse" : ""
          }`}
        >
          Progress
          <br />
          <span className="tw-text-xs tw-text-gray-500">(Last run)</span>
        </h5>
        <JobProgressbarHandler
          job={job}
          fetchingJob={fetchingJob}
          retryClicked={retryClicked}
        />
      </div>
      <div className="tw-py-6">
        <div className="tw-flex tw-flex-col tw-gap-2">
          <h5 className="tw-text-gray-600">Results</h5>

          <div className="tw-flex tw-gap-4 tw-ml-2">
            {retryableDocumentIds.length > 0 && (
              <Button
                compact
                icon
                variant="primary-alt"
                size="sm"
                onClick={() => OnRetryFailedDocuments(retryableDocumentIds)}
                disabled={retryClicked || streamIsActive}
                loading={retryClicked}
              >
                <Icon name="refresh" />
                Retry Failed Documents
              </Button>
            )}
            {job?.all_product_list_url && !streamIsActive && (
              <>
                <a
                  href={job.all_product_list_url}
                  target="_blank"
                  rel="noreferrer"
                  className="txu-btn txu-sm txu-secondary-alt txu-compact"
                >
                  View All Documents...
                </a>
              </>
            )}
            {job?.success_product_list_url && !streamIsActive && (
              <>
                <a
                  href={job.success_product_list_url}
                  target="_blank"
                  rel="noreferrer"
                  className="txu-btn txu-sm txu-secondary-alt txu-compact"
                >
                  View Successful Documents...
                </a>
              </>
            )}

            {job?.failed_product_list_url && !streamIsActive && (
              <>
                <a
                  href={job.failed_product_list_url}
                  target="_blank"
                  rel="noreferrer"
                  className="txu-btn txu-sm txu-secondary-alt txu-compact"
                >
                  View Failed Documents...
                </a>
              </>
            )}
          </div>
        </div>
        {fetchingJob ? (
          <Spinner align="left" />
        ) : (
          <JobsSummaryTableTabs
            streamIsActive={streamIsActive}
            job={job}
            retryClicked={retryClicked || streamIsActive}
            OnRetryFailedDocuments={OnRetryFailedDocuments}
          />
        )}
      </div>
      {userIsStaff && (
        <div className="txu-log-item">
          <h5 className="txu-log-item-label tw-flex tw-flex-col tw-items-start tw-gap-2">
            Arguments{" "}
            {!!shortArguments && (
              <Button
                onClick={() => setShowFullArguments(!showFullArguments)}
                size="sm"
                compact
              >
                {showFullArguments ? "Show less" : "Show more"}
              </Button>
            )}
          </h5>
          <pre className="txu-log-item-value tw-break-all tw-whitespace-pre-wrap">
            {!shortArguments
              ? fullArguments
              : showFullArguments
              ? fullArguments
              : shortArguments}
          </pre>
        </div>
      )}
    </>
  );
};
